<template>
  <div class="acc-sidebar d-flex flex-column fill-height h-100">

    <!-- Header -->
    <div class="acc-header">
      <div class="acc-title-wrapper">
        <svg class="acc-spin-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
        </svg>
        <h3 class="acc-title">Accessories</h3>
      </div>
      <button class="acc-back-btn" title="Hide accessories" @click="$emit('close')">
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
        </svg>
      </button>
    </div>

    <p class="acc-subtitle ma-0">Select items to customize your workflow</p>

    <!-- Add-ons List -->
    <div v-if="copier.datasets && copier.datasets.length" class="acc-list flex-grow-1">
      <div
        v-for="(ds, index) in paginatedDatasets"
        :key="ds.id"
        class="acc-item"
        :class="{ 'acc-item--selected': ds.selected, 'acc-item--locked': ds.default_attached == 1 }"
        :style="`animation-delay: ${0.05 + index * 0.05}s; ${ds.default_attached == 1 ? 'cursor: default;' : ''}`"
        @click="ds.default_attached == 1 ? null : $emit('toggle-dataset', ds)"
      >
        <div class="acc-item-info">
          <span class="acc-item-title" :title="CleanText(Accessory(ds).name)">
            {{ CleanText(Accessory(ds).name) }}
          </span>
          <span class="acc-item-desc">
            <template v-if="CleanText(Accessory(ds).description) && CleanText(Accessory(ds).description).toLowerCase() !== CleanText(Accessory(ds).name).toLowerCase()">
              {{ CleanText(Accessory(ds).description) }}
            </template>
            <template v-else-if="Accessory(ds).part_number">
              #{{ CleanText(Accessory(ds).part_number) }}
            </template>
            <template v-else>
              Optional add-on
            </template>
          </span>
        </div>

        <!-- Action Button -->
        <button
          class="acc-action-btn"
          :class="{
            'acc-action-btn--selected': ds.selected && ds.default_attached != 1,
            'acc-action-btn--locked': ds.selected && ds.default_attached == 1
          }"
          :title="ds.selected && ds.default_attached == 1 ? 'Standard attached' : ds.selected ? 'Remove accessory' : 'Add accessory'"
        >
          <!-- Locked icon -->
          <svg v-if="ds.selected && ds.default_attached == 1" width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          <!-- Plus / X icon (rotated via CSS) -->
          <svg v-else width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M12 4v16m8-8H4"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="flex-grow-1 d-flex flex-column align-center justify-center px-4 py-10 text-center">
      <v-icon icon="mdi-puzzle-outline" size="48" color="grey-lighten-1" class="mb-3" />
      <span class="text-body-2 font-weight-bold text-grey-darken-1 mb-1">No accessories available</span>
      <span class="text-caption text-grey">This machine doesn't have any optional add-ons.</span>
    </div>

    <!-- Pagination Controls -->
    <div v-if="totalPages > 1" class="acc-pagination">
      <v-pagination
        v-model="currentPageModel"
        :length="totalPages"
        :total-visible="3"
        density="compact"
        active-color="primary"
      />
    </div>

  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

const props = defineProps<{
  copier: {
    instance: any
    datasets: any[]
    accessories: any[]
  }
  currentPage: number
  itemsPerPage?: number
}>()

const emit = defineEmits<{
  (e: 'update:currentPage', val: number): void
  (e: 'toggle-dataset', ds: any): void
  (e: 'close'): void
}>()

const currentPageModel = computed({
  get: () => props.currentPage,
  set: (val: number) => emit('update:currentPage', val)
})

const perPage = computed(() => props.itemsPerPage || 8)

const sortedDatasets = computed(() => {
  if (!props.copier.datasets) return []
  return [...props.copier.datasets].sort((a, b) => {
    const name_a = CleanText(Accessory(a).name).toLowerCase()
    const name_b = CleanText(Accessory(b).name).toLowerCase()
    if (name_a < name_b) return -1
    if (name_a > name_b) return 1
    return 0
  })
})

const totalPages = computed(() => {
  return Math.ceil(sortedDatasets.value.length / perPage.value)
})

const paginatedDatasets = computed(() => {
  const start = (props.currentPage - 1) * perPage.value
  return sortedDatasets.value.slice(start, start + perPage.value)
})

function CleanText(str: any) {
  if (!str) return ''
  return String(str).replace(/<[^>]*>?/gm, '').replace(/\s+/g, ' ').trim()
}

function Accessory(dataset: any) {
  return props.copier.accessories.find((acc: any) => acc.id === dataset.accessory_id) || { name: '', part_number: '', advertising_price: 0, description: '' }
}
</script>

<style scoped lang="scss">
.acc-sidebar {
  position: relative;
  overflow: hidden;

  & > * {
    position: relative;
    z-index: 1;
  }
}

/* ---- Header ---- */
.acc-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 4px;
}

.acc-title-wrapper {
  display: flex;
  align-items: center;
  gap: 10px;
}

.acc-spin-icon {
  color: #3b82f6;
  width: 26px;
  height: 26px;
  flex-shrink: 0;
  animation: acc-spin 10s linear infinite;
}

@keyframes acc-spin {
  100% { transform: rotate(360deg); }
}

.acc-title {
  font-size: 1.25rem;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #0f172a;
  margin: 0;
}

.acc-back-btn {
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(203, 213, 225, 0.6);
  color: #0f172a;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s ease, transform 0.2s ease;
  flex-shrink: 0;

  &:hover {
    background: #ffffff;
    transform: scale(1.06);
  }
}

.acc-subtitle {
  color: #64748b;
  font-size: 0.88rem;
  font-weight: 300;
  margin-bottom: 16px;
}

/* ---- List ---- */
.acc-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
  padding: 4px 4px 4px 2px;
  margin: -4px -4px -4px -2px;

  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.1) transparent;

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: rgba(0,0,0,0.02); border-radius: 10px; }
  &::-webkit-scrollbar-thumb { background: rgba(0,0,0,0.1); border-radius: 10px; }
  &::-webkit-scrollbar-thumb:hover { background: rgba(0,0,0,0.2); }
}

/* ---- Item Cards ---- */
.acc-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(203, 213, 225, 0.6);
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  animation: acc-slide-up 0.45s cubic-bezier(0.4, 0, 0.2, 1) backwards;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at center, rgba(0,0,0,0.02) 0%, transparent 100%);
    opacity: 0;
    transition: opacity 0.3s;
    pointer-events: none;
  }

  &:hover {
    background: rgba(255, 255, 255, 1);
    transform: translateY(-2px);
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08);

    &::after { opacity: 1; }

    .acc-action-btn:not(.acc-action-btn--selected):not(.acc-action-btn--locked) {
      background: #3b82f6;
      color: white;
    }
  }

  &--selected {
    background: rgba(59, 130, 246, 0.08) !important;
    border-color: #3b82f6 !important;

    .acc-item-title { color: #2563eb; }
  }

  &--locked {
    cursor: default;
  }
}

@keyframes acc-slide-up {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

.acc-item-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  flex-grow: 1;
  padding-right: 12px;
}

.acc-item-title {
  font-size: 0.93rem;
  font-weight: 500;
  color: #0f172a;
  transition: color 0.2s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.acc-item-desc {
  font-size: 0.8rem;
  color: #64748b;
  font-weight: 300;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ---- Action Button ---- */
.acc-action-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 2px solid #3b82f6;
  background: transparent;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  cursor: pointer;
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);

  svg {
    transition: transform 0.28s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &--selected {
    background: #10b981;
    border-color: #10b981;
    color: white;

    svg { transform: rotate(45deg); }
  }

  &--locked {
    background: #f1f5f9;
    border-color: #cbd5e1;
    color: #94a3b8;
    cursor: default;
  }
}

/* ---- Pagination ---- */
.acc-pagination {
  display: flex;
  justify-content: center;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}
</style>
